import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RoomLayoutEntityHandle } from "building-types/roomLayouts/roomLayoutTypes";
import { docGetEntityHandleById } from "./entity/docGetEntityHandleById";
import { docGetGeneratedRoomLayoutEligibleHandles } from "./rooms/layout/docGetGeneratedRoomLayoutEligibleHandles";
import { isRoomLayoutEntityHandle } from "./rooms/layout/roomLayoutTools";

export function docNormalizeEntityIdToHandles<const T extends readonly DocEntityType[]>(
  doc: Doc,
  entityIds: string | string[],
  entityTypes: T,
  handleTypeGuard?: (handle: DocEntityHandle) => handle is Extract<DocEntityHandle, { type: T[number] }>,
): Extract<DocEntityHandle, { type: T[number] }>[] {
  const handles = Array.isArray(entityIds) ? entityIds : [entityIds];
  const eligibleHandles = handles
    .map((id) => docGetEntityHandleById(doc, id, [...entityTypes]))
    .filter(Boolean) as DocEntityHandle[];
  return docNormalizeHandles(doc, eligibleHandles, entityTypes, handleTypeGuard);
}

export function docNormalizeHandles<const T extends readonly DocEntityType[]>(
  doc: Doc,
  handles: DocEntityHandle | DocEntityHandle[],
  entityTypes: T,
  handleTypeGuard?: (handle: DocEntityHandle) => handle is Extract<DocEntityHandle, { type: T[number] }>,
): Extract<DocEntityHandle, { type: T[number] }>[] {
  let normalizedHandles = Array.isArray(handles) ? handles : [handles];

  if (normalizedHandles.every(isRoomLayoutEntityHandle)) {
    normalizedHandles = docGetGeneratedRoomLayoutEligibleHandles(doc, normalizedHandles as RoomLayoutEntityHandle[]);
  }

  normalizedHandles = normalizedHandles.filter((handle) => entityTypes.includes(handle.type));

  if (handleTypeGuard) {
    normalizedHandles = normalizedHandles.filter(handleTypeGuard);
  }

  return normalizedHandles as Extract<DocEntityHandle, { type: T[number] }>[];
}
